import { CommonModule } from "@angular/common";
import { Component, EventEmitter, OnInit, Output, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { InputTextModule } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { FormsModule } from "@angular/forms";
import { LotSearchResults } from "../lot-search-results/lot-search.component";
import { firstValueFrom } from "rxjs";
import { SkeletonModule } from "primeng/skeleton";
import { NetworkStatusService } from "../../../../../shared/services/network-status.service";
import { DivisionDto } from "../../../../../domains/divisions/models/divisionDto";
import { DivisionService } from "../../../../../domains/divisions/services/division.service";
import { LotSearchService } from "../services/lot-search.service";
import { SearchOfflineComponent } from "../../../../../shared/components/offline-search/offline-search.component";

export interface Community {
  salesCommunityName: string;
  externalSalesCommunityId: number;
}

type SearchStep = "division" | "community" | "search";

@Component({
  selector: "cm-lot-search-drawer",
  standalone: true,
  imports: [
    CommonModule,
    SelectModule,
    InputTextModule,
    FormsModule,
    LotSearchResults,
    SkeletonModule,
    SearchOfflineComponent,
  ],
  templateUrl: "./cm-lot-search.component.html",
  styleUrl: "./cm-lot-search.component.scss"
})
export class CmLotSearchComponent implements OnInit {
  @Output() onSheetClose = new EventEmitter<void>();
  @Output() sheetModeChange = new EventEmitter<"sheet" | "fullscreen">();
  @Output() done = new EventEmitter<void>();

  step = signal<SearchStep>("division");

  hasDetails = signal(false);
  isOpen = signal(false);

  selectedCommunity = signal<Community | undefined>(undefined);
  drawerSelectedCommunity: Community | undefined;
  drawerCommunityResults = signal<Community[]>([]);

  divisions = signal<DivisionDto[]>([]);
  selectedDivision = signal<DivisionDto | undefined>(undefined);
  disableDivisionDropdown = signal(false);

  loading = true;
  loadingDivisions = true;
  communities: Community[] = [];
  searchTerm = "";
  communitySearchTerm = "";
  isOnline = true;

  constructor(
    private lotSearchService: LotSearchService,
    private divisionService: DivisionService,
    private networkService: NetworkStatusService,
  ) {
    this.networkService.online$.pipe(takeUntilDestroyed()).subscribe(val => {
      this.isOnline = val;
    });
  }

  async ngOnInit(): Promise<void> {
    this.setStep("division");

    if (this.isOnline === false) {
      this.loading = false;
      this.loadingDivisions = false;
      return;
    }

    this.loading = true;
    this.loadingDivisions = true;

    try {
      const divisions = await firstValueFrom(this.divisionService.getDivisionsForUser());
      this.divisions.set(divisions ?? []);

      if (this.divisions().length === 1) {
        this.disableDivisionDropdown.set(true);
        await this.onDivisionSelected(this.divisions()[0]);
      } else {
        this.disableDivisionDropdown.set(false);
        this.resetDivisionAndCommunityState();
        this.setStep("division");
      }
    } finally {
      this.loading = false;
      this.loadingDivisions = false;
    }
  }

  async executeSearch(): Promise<void> {
    this.hasDetails.set(true);
  }

  toCommunitySearch(): void {
    this.hasDetails.set(false);
    this.setStep("search");
  }

  openCommunitySearch(): void {
    if (this.isCommunityDisabled() || this.loading) {
      return;
    }

    this.isOpen.set(true);
  }

  onCancel(): void {
    this.isOpen.set(false);
  }

  onSelect(): void {
    this.selectedCommunity.set(this.drawerSelectedCommunity);
    this.isOpen.set(false);

    if (this.selectedCommunity()) {
      this.setStep("search");
    }
  }

  onDrawerSearchTermChange(value: string): void {
    this.communitySearchTerm = value;

    const results = this.communities.filter(community =>
      community.salesCommunityName.toLowerCase().includes(value.toLowerCase())
    );

    this.drawerCommunityResults.set(results);
  }

  isCommunityDisabled(): boolean {
    const requiresDivisionSelection = this.divisions().length > 1;
    return this.loading || (requiresDivisionSelection && !this.selectedDivision());
  }

  isDivisionDisabled(): boolean {
    const hasSingleDivision = this.divisions().length === 1;
    return this.loadingDivisions || hasSingleDivision;
  }

  async onDivisionSelected(division: DivisionDto | undefined): Promise<void> {
    this.selectedDivision.set(division);

    this.selectedCommunity.set(undefined);
    this.drawerSelectedCommunity = undefined;
    this.communitySearchTerm = "";

    this.communities = [];
    this.drawerCommunityResults.set([]);

    if (!division) {
      this.setStep("division");
      return;
    }

    this.loading = true;

    try {
      await this.loadCommunitiesForDivision(division.externalDivisionId);
      this.setStep("community");
    } finally {
      this.loading = false;
    }
  }

  onCommunityRowClick(community: Community): void {
    this.drawerSelectedCommunity = community;
    this.onSelect();
  }

  backFromCommunity(): void {
    this.setStep("division");
  }

  backFromSearch(): void {
    this.setStep("community");
  }

  backFromResults(): void {
    this.setStep("search");
  }

  closeSheet(): void {
    this.onSheetClose.emit();
  }

  private async loadCommunitiesForDivision(externalDivisionId: number): Promise<void> {
    const communities = await firstValueFrom(
      this.lotSearchService.getSalesCommunitiesByDivision(externalDivisionId)
    );

    this.communities = (communities ?? []).slice().sort((a, b) =>
      a.salesCommunityName.localeCompare(b.salesCommunityName)
    );

    this.drawerCommunityResults.set(this.communities);
  }

  private resetDivisionAndCommunityState(): void {
    this.selectedDivision.set(undefined);
    this.selectedCommunity.set(undefined);
    this.drawerSelectedCommunity = undefined;
    this.communities = [];
    this.drawerCommunityResults.set([]);
    this.communitySearchTerm = "";
    this.searchTerm = "";
    this.hasDetails.set(false);
  }

  private setStep(step: SearchStep): void {
    this.step.set(step);

    if (step === "division" || step === "community") {
      this.sheetModeChange.emit("sheet");
      this.hasDetails.set(false);
    } else {
      this.sheetModeChange.emit("fullscreen");
    }
  }
}